'use client';

import { Embed, useChatflows } from 'chatflows-embed';
import { useAuth } from '@/contexts/AuthContext';
import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

/**
 * ChatFlows Widget Component
 *
 * This component integrates ChatFlows AI assistant into the application.
 * It provides contextual AI assistance based on the current user and page.
 *
 * Features:
 * - Automatic user context (email, name, role)
 * - Page-aware AI responses
 * - Floating chat widget
 * - Custom functions for AI to call
 * - Hidden on compliance pages (they have their own embedded ChatFlows)
 */
export function ChatFlowsWidget() {
  const { user, userProfile, company } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Only render if we have user data
  if (!user) return null;

  // Hide widget on compliance pages (they have their own embedded ChatFlows)
  // and on kiosk pages (standalone kiosk UI, no chat needed)
  const isCompliancePage = location.pathname.includes('/compliance/');
  const isKioskPage = location.pathname.startsWith('/kiosk');
  if (isCompliancePage || isKioskPage) return null;

  return (
    <>
      <Embed
        publicKey='2c41f6dfdb7de3d5'
        apiUrl='https://chatflows.add.ai'
        position='bottom-right'
        draggable={true}
        offsetX='20px'
        offsetY='20px'
        buttonSize='60px'
        user={{
          id: user.id,
          email: user.email,
          name: userProfile?.full_name || user.email,
          metadata: {
            role: userProfile?.role || 'User',
            company: company?.name || 'Unknown',
            companyId: company?.id || null,
            page: window.location.pathname,
            appName: 'Training Matrix V2',
            appType: 'Training Compliance & Skills Management'
          }
        }}
        onReady={() => {
          console.log('✅ ChatFlows widget ready!');
        }}
        onError={error => {
          console.error('❌ ChatFlows widget error:', error);
        }}
      />
      <ChatFlowsFunctions
        navigate={navigate}
        user={user}
        userProfile={userProfile}
        company={company}
      />
    </>
  );
}

/**
 * Component to register custom functions that the AI can call
 */
function ChatFlowsFunctions({ navigate, user, userProfile, company }) {
  const { registerFunction } = useChatflows();

  useEffect(() => {
    // Register custom function: Create Space
    registerFunction('createSpace', async args => {
      console.log('AI called createSpace with:', args);
      navigate('/create-space');
      return { success: true, message: 'Navigating to Create Space page' };
    });

    // Register custom function: Navigate
    registerFunction('navigateToPage', async args => {
      console.log('AI called navigateToPage with:', args);
      const { page } = args;
      if (page) {
        navigate(page);
        return { success: true, message: `Navigating to ${page}` };
      }
      return { success: false, message: 'No page specified' };
    });

    // Register custom function: List User Spaces
    registerFunction('listUserSpaces', async args => {
      console.log('AI called listUserSpaces with:', args);

      if (!company?.id) {
        return {
          success: false,
          message: 'No company found',
          spaces: []
        };
      }

      try {
        // Fetch user's spaces/projects
        const { data: projects, error } = await supabase
          .from('projects')
          .select(
            'id, name, type, description, location, industry, created_date'
          )
          .eq('company_id', company.id)
          .eq('is_archived', false)
          .order('created_date', { ascending: false })
          .limit(50); // Limit to 50 most recent spaces

        if (error) {
          console.error('Error fetching spaces:', error);
          return {
            success: false,
            message: 'Failed to fetch spaces',
            error: error.message,
            spaces: []
          };
        }

        // Format the spaces for the AI
        const formattedSpaces = projects.map(p => ({
          id: p.id,
          name: p.name,
          type: p.type,
          description: p.description,
          location: p.location,
          industry: p.industry,
          createdDate: p.created_date,
          url: `/space/${p.id}`
        }));

        return {
          success: true,
          message: `Found ${formattedSpaces.length} spaces`,
          spaces: formattedSpaces,
          total: formattedSpaces.length
        };
      } catch (error) {
        console.error('Error in listUserSpaces:', error);
        return {
          success: false,
          message: 'An error occurred while fetching spaces',
          error: error.message,
          spaces: []
        };
      }
    });

    // Register custom function: Go To Space
    registerFunction('goToSpace', async args => {
      console.log('AI called goToSpace with:', args);
      const { id, spaceId, space_name, name } = args;

      // Handle both ID-based and name-based navigation
      let targetId = id || spaceId;
      const spaceName = space_name || name;

      if (!company?.id) {
        return {
          success: false,
          message: 'No company found'
        };
      }

      try {
        // If space name is provided, look up the ID
        if (spaceName && !targetId) {
          const { data: space, error: lookupError } = await supabase
            .from('projects')
            .select('id, name')
            .eq('company_id', company.id)
            .eq('is_archived', false)
            .ilike('name', spaceName) // Case-insensitive match
            .single();

          if (lookupError || !space) {
            // Try partial match if exact match fails
            const { data: spaces, error: partialError } = await supabase
              .from('projects')
              .select('id, name')
              .eq('company_id', company.id)
              .eq('is_archived', false)
              .ilike('name', `%${spaceName}%`)
              .limit(1);

            if (partialError || !spaces || spaces.length === 0) {
              return {
                success: false,
                message: `Could not find a space named "${spaceName}". Please check the name and try again.`
              };
            }

            targetId = spaces[0].id;
          } else {
            targetId = space.id;
          }
        }

        if (!targetId) {
          return {
            success: false,
            message: 'No space ID or name provided'
          };
        }

        // Verify the space exists and user has access
        const { data: space, error } = await supabase
          .from('projects')
          .select('id, name')
          .eq('id', targetId)
          .eq('company_id', company.id)
          .eq('is_archived', false)
          .single();

        if (error || !space) {
          return {
            success: false,
            message: `Space not found or you don't have access to it`
          };
        }

        // Navigate to the space
        navigate(`/space/${targetId}`);

        return {
          success: true,
          message: `Successfully navigated to ${space.name}`,
          spaceName: space.name,
          spaceId: targetId
        };
      } catch (error) {
        console.error('Error in goToSpace:', error);
        return {
          success: false,
          message: 'An error occurred while navigating to space',
          error: error.message
        };
      }
    });

    // Register custom function: Show Notification
    registerFunction('showNotification', async args => {
      console.log('AI called showNotification with:', args);
      const { message, type = 'info', duration = 3000 } = args;

      if (!message) {
        return { success: false, message: 'No notification message provided' };
      }

      // Create and show a toast notification
      const notification = document.createElement('div');
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        padding: 16px 24px;
        background: ${
          type === 'error'
            ? '#ef4444'
            : type === 'success'
            ? '#10b981'
            : type === 'warning'
            ? '#f59e0b'
            : '#3b82f6'
        };
        color: white;
        border-radius: 8px;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        z-index: 10000;
        font-family: system-ui, -apple-system, sans-serif;
        font-size: 14px;
        max-width: 400px;
        animation: slideIn 0.3s ease-out;
      `;
      notification.textContent = message;

      // Add animation
      const style = document.createElement('style');
      style.textContent = `
        @keyframes slideIn {
          from { transform: translateX(400px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideOut {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(400px); opacity: 0; }
        }
      `;
      document.head.appendChild(style);

      document.body.appendChild(notification);

      // Auto-remove after duration
      setTimeout(() => {
        notification.style.animation = 'slideOut 0.3s ease-out';
        setTimeout(() => {
          document.body.removeChild(notification);
        }, 300);
      }, duration);

      return {
        success: true,
        message: `Notification shown: "${message}"`,
        type,
        duration
      };
    });

    console.log('ChatFlows custom functions registered');
  }, [registerFunction, navigate, company]);

  return null;
}
